import { useEffect, useState } from 'react';
import { GROUP_MAX_MEMBERS, GROUP_TITLE_MAX_LENGTH } from '@campus/core';
import { Button, Icon, Input } from '../ds/index.js';
import { PersonAvatar } from '../people/PersonAvatar.js';
import { useMembersStore, sessionInfo, loadMembers } from '../people/membersStore.js';
import { useCampusOnline } from '../people/presence.js';
import { MessagingApiError } from '../../messaging/client.js';
import { createGroup } from '../../messaging/store.js';
import { sendErrorLabel } from './format.js';

/**
 * Choisir plusieurs personnes de l'annuaire (création d'un groupe, ajout).
 * `exclude` : déjà dans le groupe. En ligne d'abord, puis par nom.
 */
export function MemberMultiPicker({ selected, onToggle, exclude = [], max }: {
  selected: string[];
  onToggle: (userId: string) => void;
  exclude?: string[];
  /** Personnes qu'on peut encore choisir. */
  max: number;
}) {
  const members = useMembersStore((s) => s.members);
  const online = useCampusOnline();
  const [query, setQuery] = useState('');
  const { userId: me } = sessionInfo();
  useEffect(() => { void loadMembers(); }, []);
  const q = query.trim().toLocaleLowerCase('fr');
  const list = [...members.values()]
    .filter((m) => m.userId !== me && !exclude.includes(m.userId) && (!q || m.displayName.toLocaleLowerCase('fr').includes(q)))
    .sort((a, b) => Number(online.has(b.userId)) - Number(online.has(a.userId)) || a.displayName.localeCompare(b.displayName, 'fr'));
  const full = selected.length >= max;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{ padding: '0 16px 8px' }}>
        <Input aria-label="Chercher une personne" placeholder="Chercher une personne…" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: '0 8px 8px', overflowY: 'auto', flex: 1 }}>
        {list.map((m) => {
          const on = selected.includes(m.userId);
          return (
            <li key={m.userId}>
              <button type="button" className="pl-menu-item" aria-pressed={on} disabled={!on && full}
                onClick={() => onToggle(m.userId)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: '4px 8px', borderRadius: 'var(--radius-md)', textAlign: 'left' }}>
                <PersonAvatar userId={m.userId} name={m.displayName} size={32} />
                <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 600 }}>{m.displayName}</span>
                {online.has(m.userId) && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>en ligne</span>}
                <span aria-hidden="true" className={`pl-pick${on ? ' is-on' : ''}`}>{on && <Icon name="check" size={14} />}</span>
              </button>
            </li>
          );
        })}
        {list.length === 0 && <p className="pl-help" style={{ margin: 8 }}>{q ? 'Personne de ce nom.' : 'Plus personne à ajouter.'}</p>}
      </ul>
    </div>
  );
}

/** Nouveau groupe : un nom, des personnes, puis « Créer le groupe ». */
export function GroupCreator({ onCreated, onCancel }: { onCreated: (id: string) => void; onCancel: () => void }) {
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const members = useMembersStore((s) => s.members);
  const toggle = (id: string) => setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const missing = !title.trim() ? 'Donne un nom au groupe, dans le champ tout en haut.'
    : selected.length === 0 ? 'Choisis au moins une personne dans la liste.'
    : null;
  const ready = !missing && !busy;

  const create = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      onCreated(await createGroup(title.trim(), selected));
    } catch (err) {
      setError(sendErrorLabel(err instanceof MessagingApiError ? err.code : 'NETWORK').replace(/^Pas envoyé/, 'Groupe pas créé'));
      setBusy(false);
    }
  };

  return (
    <form style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }} onSubmit={(e) => { e.preventDefault(); void create(); }}>
      <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button variant="ghost" size="sm" icon="arrow-left" aria-label="Retour" onClick={onCancel} />
        <h3 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 700 }}>Nouveau groupe</h3>
      </div>
      <div style={{ padding: '0 16px 12px' }}>
        <Input autoFocus aria-label="Nom du groupe" placeholder="Nom du groupe" maxLength={GROUP_TITLE_MAX_LENGTH}
          value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <MemberMultiPicker selected={selected} onToggle={toggle} max={GROUP_MAX_MEMBERS - 1} />
      <div style={{ padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--color-border-default)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* En bas, sous la liste : choisir quelqu'un ne la fait pas bouger sous la souris. */}
        {selected.length > 0 && (
          <div className="pl-choices" aria-label="Personnes choisies" style={{ maxHeight: 76, overflowY: 'auto' }}>
            {selected.map((id) => (
              <button key={id} type="button" className="pl-choice" aria-checked="true" role="checkbox" onClick={() => toggle(id)}
                title="Retirer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {members.get(id)?.displayName ?? 'Membre'} <Icon name="x" size={12} />
              </button>
            ))}
          </div>
        )}
        {/* Pourquoi le bouton est grisé : le champ du nom, tout en haut, passait inaperçu. */}
        {missing && !error && (
          <p className="pl-help" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--color-danger)', flexShrink: 0 }}><Icon name="ban" size={14} /></span>
            {missing}
          </p>
        )}
        {error && <p role="alert" className="pl-help" style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p>}
        <Button variant="primary" type="submit" icon="users" disabled={!ready} style={{ width: '100%' }}>
          {selected.length > 0 ? `Créer le groupe (${selected.length + 1} personnes)` : 'Créer le groupe'}
        </Button>
      </div>
    </form>
  );
}
